import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Shield, ArrowLeft, Lock, Lightning } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import usePageMeta from "@/hooks/usePageMeta";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [owned, setOwned] = useState(false);

  usePageMeta(
    product?.title || "Provision Detail",
    product ? `${product.tagline} ${product.description}`.slice(0, 155) : "EIGOO Provisions — armored digital product details."
  );

  useEffect(() => {
    api.get("/products").then((r) => {
      const p = r.data.products.find((x) => x.slug === slug);
      setProduct(p);
    });
  }, [slug]);

  useEffect(() => {
    if (user && product) {
      api.get("/vault/purchases").then((r) => {
        const has = r.data.purchases.some((p) => p.product_id === product.id);
        setOwned(has);
      }).catch(() => {});
    }
  }, [user, product]);

  if (!product) {
    return <div className="py-32 text-center text-white/40 overline" data-testid="product-loading">Loading provision...</div>;
  }

  const handleCheckout = async () => {
    if (!user || user === false) {
      navigate("/login", { state: { from: `/products/${slug}` } });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        product_id: product.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  const isFlagship = product.is_flagship;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12" data-testid="product-detail-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 hover:text-[#FFD700] mb-8"
      >
        <ArrowLeft size={14} /> Back to Arsenal
      </button>

      <div className="grid lg:grid-cols-12 gap-12">
        {/* Visual */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="surface-card armor-border aspect-[4/3] relative overflow-hidden"
          >
            {isFlagship ? (
              <>
                <img src={product.image} alt={product.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 to-transparent" />
              </>
            ) : (
              <div className="absolute inset-0 tactical-grid flex items-center justify-center">
                <div className="text-center">
                  <Shield size={80} weight="duotone" className="text-[#FFD700] mx-auto mb-4 animate-float" />
                  <div className="overline">{product.category}</div>
                </div>
              </div>
            )}
            <div className="absolute top-6 left-6 overline px-3 py-1.5 bg-black/50 backdrop-blur-md border border-[#FFD700]/40">
              {product.sector}
            </div>
          </motion.div>

          {/* Features */}
          <div className="mt-10">
            <h3 className="overline mb-6">// Armored Provisions Include</h3>
            <ul className="space-y-3">
              {product.features.map((f, i) => (
                <li key={i} className="flex items-start gap-3 p-3 border-l-2 border-[#FFD700]/30">
                  <CheckCircle size={20} weight="duotone" className="text-[#FFD700] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-white/80 font-light">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-5">
          <span className="overline">{product.category}</span>
          <h1 className="heading-display text-4xl md:text-5xl mt-3 mb-4" data-testid="product-title">{product.title}</h1>
          <p className="text-lg text-white/70 font-light leading-relaxed mb-8">{product.tagline}</p>

          <div className="surface-card p-6 mb-6">
            <div className="overline mb-2">Provision Rate</div>
            <div className="flex items-baseline gap-3 mb-2">
              <span className="font-mono text-4xl md:text-5xl font-bold text-[#FFD700] text-glow-gold" data-testid="product-price">
                ${product.price_usd}
              </span>
              <span className="text-xs uppercase tracking-widest text-white/40">USD // ONE-TIME SETUP</span>
            </div>
            {product.is_subscription && product.subscription_price_usd && (
              <div className="flex items-baseline gap-2 mb-6 pb-4 border-b border-white/5" data-testid="product-subscription">
                <span className="font-mono text-xl text-white/80 font-bold">+ ${product.subscription_price_usd}</span>
                <span className="font-mono text-xs uppercase tracking-widest text-white/50">
                  {product.subscription_label || "/mo"} (billed separately)
                </span>
              </div>
            )}
            {!product.is_subscription && <div className="mb-6"></div>}
            {owned ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[#FFD700] text-sm font-semibold uppercase tracking-wider">
                  <CheckCircle weight="fill" /> Provisioned. In your vault.
                </div>
                <button onClick={() => navigate("/commander")} className="w-full btn-ghost py-3 text-xs" data-testid="goto-vault-btn">
                  Open Commander Deck
                </button>
              </div>
            ) : (
              <button
                onClick={handleCheckout}
                disabled={loading}
                data-testid="checkout-btn"
                className="w-full btn-gold py-4 text-xs flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {loading ? (
                  <>Establishing secure channel...</>
                ) : (
                  <>
                    <Lock size={16} weight="bold" />
                    Engage Secure Checkout
                  </>
                )}
              </button>
            )}
            <div className="text-[0.65rem] font-mono uppercase tracking-widest text-white/40 mt-4 text-center">
              Stripe-secured // Lifetime Vault Access
            </div>
          </div>

          {/* Description */}
          <div className="space-y-4">
            <h3 className="overline">// Briefing</h3>
            <p className="text-sm text-white/70 font-light leading-relaxed">{product.description}</p>
          </div>

          {/* Sector starter additional info */}
          {!isFlagship && (
            <div className="mt-8 p-5 border border-[#FFD700]/20 bg-[#FFD700]/5">
              <div className="flex items-start gap-3">
                <Lightning size={20} weight="duotone" className="text-[#FFD700] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-white text-sm mb-1">Pairs with Blueprint Engine</div>
                  <div className="text-xs text-white/60 font-light">
                    Stack this Starter with an AI-generated Sovereign Blueprint for full operational coverage.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
